import type { BookingDraft, ConfirmationDetails, ServiceMappers } from '../../domain/types'
import type { CheckoutPriceOverviewSubItems } from '../../domain/types/priceOverview'
import type { BookingDraftErrorMapper } from './model-mappers/bookingDraftErrorMapper'
import type { ServiceItem } from '@/cms/fields/types/checkout-page'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import { toSnakeCase } from '@/helpers/string-helpers'
import { mapPhoneNumber } from '@/scopes/checkout/infrastructure/api/mapPhoneNumber'
import {
  type APIBookingDraft,
  type APIBookingDraftAccommodationDates,
  type APIBookingDraftAgeCategoryRestriction,
  APIBookingDraftBusSummaryComponent,
  APIBookingDraftGenericSummaryComponent,
  type APIBookingDraftParticipant,
  type APIBookingDraftPriceOverviewItem,
  type APIBookingDraftPriceOverviewSubItems,
  type APIBookingDraftPricesSummary,
  type APIBookingDraftServiceOverview,
  APIBookingDraftTransportInfo,
  type APIBookingDraftTransportLeg,
  type ServiceRenderType,
  ServiceRenderTypeEnum,
  ServiceTypeApi,
} from '@/scopes/checkout/infrastructure/api/types'
import { parseDateForLocalFormatting } from '@/scopes/common/domain/utils/dates'
import { ServiceType } from '../../domain/types'
import { type GA4ImpressionItemsConfig, mapDefaultGA4ImpressionItems } from './model-mappers/ga4AnalyticsMapper'
import { createServiceOptionMapper } from './model-mappers/serviceOptionMapper'

export interface BookingDraftResponseMapper {
  map: (bookingDraft: APIBookingDraft, configData?: ServiceItem[]) => BookingDraft
}

interface Dependencies {
  serviceMappers: ServiceMappers
  errorMapper: BookingDraftErrorMapper
}

export function createBookingDraftResponseMapper(dependencies: Dependencies): BookingDraftResponseMapper {
  const { serviceMappers: mappers, errorMapper } = dependencies

  const map = (bookingDraft: APIBookingDraft, configData?: ServiceItem[]) => {
    const missingServices = configData?.filter(service => service.type === 'GenericService') ?? []

    const mapStandardServices = Object.entries(mappers).reduce((acc, [serviceType, serviceMapper]) => {
      const mappedService = serviceMapper.map(bookingDraft)
      return mappedService ? { ...acc, [serviceType]: mappedService } : acc
    }, {})

    const mappedMissingServices = missingServices.length
      ? missingServices.reduce(
          (acc, service) => {
            const serviceData = bookingDraft.services.find(item => item.aName === service.aName)
            if (!serviceData || !serviceData.aName)
              return acc

            const mapByRenderType = (
              renderType: ServiceRenderType,
              acc2: {
                genericServicePerBooking: object
                genericServicePerParticipant: object
                genericServicePerRoom: object
              },
            ) => {
              const type = getGenericServiceType(renderType)
              const mapper = mappers[type]
              if (!mapper)
                return acc2
              const mapped = mapper?.map(bookingDraft, serviceData.aName)
              return mapped
                ? {
                    ...acc2,
                    [type]: {
                      ...(acc2[type] || {}),
                      [mapped.id]: mapped,
                    },
                  }
                : acc2
            }
            if (
              service.serviceConfigurations?.showToggleForAllParticipants
              && service.renderType !== ServiceRenderTypeEnum.PerRoom
            ) {
              const alternateRenderTypeService
                = service.renderType === ServiceRenderTypeEnum.PerParticipant
                  ? ServiceRenderTypeEnum.PerBooking
                  : ServiceRenderTypeEnum.PerParticipant
              const alternateService = mapByRenderType(alternateRenderTypeService, acc)
              return mapByRenderType(service.renderType as ServiceRenderType, alternateService)
            }

            return mapByRenderType(service.renderType as ServiceRenderType, acc)
          },
          {
            [ServiceType.GenericServicePerBooking]: {},
            [ServiceType.GenericServicePerParticipant]: {},
            [ServiceType.GenericServicePerRoom]: {},
          },
        )
      : {}

    return {
      services: {
        ...mapStandardServices,
        ...mappedMissingServices,
      },
      legacyBasketInformation: mapLegacyBasketInformation(bookingDraft),
      basketInformation: mapNewBasketInformation(bookingDraft),
      isRebooking: bookingDraft.isRebooking,
      messages: mapConditionalMessages(bookingDraft),
      navigation: mapNavigation(bookingDraft),
      participants: mapParticipants(bookingDraft),
      rooms: mapRooms(bookingDraft),
      error: errorMapper.map(bookingDraft?.error),
      groupInformation: mapGroupInformation(bookingDraft),
      draftId: bookingDraft.draftId,
      analytics: mapECommerceAnalytics(bookingDraft),
      summaries: mapSummaries(bookingDraft),
      ...(bookingDraft.bookingUsps && { usps: mapUsps(bookingDraft) }),
      configurations: mapConfigurations(bookingDraft),
      ...(bookingDraft.priceOverviews && { priceOverviews: mapPriceOverviews(bookingDraft) }),
      couponCodeSuccess: textToBoolean(bookingDraft.consequences?.couponcodeSuccess ?? ''),
      ...(bookingDraft.participantsBadge?.priceChangedMessage && {
        participantBadgeWarning: mapParticipantBadgeWarning(bookingDraft),
      }),
      ...(bookingDraft.preferences
        && bookingDraft.preferences.length > 0 && { preferences: mapPreferences(bookingDraft) }),
      ...(bookingDraft.transportInfo && { transportInfo: mapTransportInfo(bookingDraft.transportInfo) }),
      ga4AncillaryImpressions: mapGA4AncillaryImpressions(bookingDraft, mappers, configData),
      ...(bookingDraft.confirmationComponents?.[0]?.bookingNumber != null && {
        confirmation: mapConfirmationDetails(bookingDraft),
      }),
    } as BookingDraft
  }

  return {
    map,
  }
}

function textToBoolean(text: string) {
  if (typeof text !== 'string')
    return false
  return text.trim().toLowerCase() === 'true'
}

function getGenericServiceType(renderType: ServiceRenderType) {
  switch (renderType) {
    case ServiceRenderTypeEnum.PerParticipant:
      return ServiceType.GenericServicePerParticipant
    case ServiceRenderTypeEnum.PerBooking:
      return ServiceType.GenericServicePerBooking
    case ServiceRenderTypeEnum.PerRoom:
      return ServiceType.GenericServicePerRoom
    default:
      return ServiceType.GenericServicePerBooking
  }
}
function mapLegacyBasketInformation(bookingDraft: APIBookingDraft) {
  const { accommodationInfo, bookingInfo } = bookingDraft
  if (!accommodationInfo || !bookingInfo)
    return {}

  return {
    accommodationName: accommodationInfo.name,
    rating: accommodationInfo.numberOfStars,
    location: accommodationInfo.location
      .map(location => location)
      .reverse()
      .join(', '),
    imageUrl: accommodationInfo.mainImage?.original,
    servicesIncluded: bookingInfo.includedServices,
    departureDate: bookingInfo.departureDate,
    duration: bookingInfo.duration,
    occupancy: bookingInfo.occupancy,
    totalPrice: bookingInfo.totalPrice,
    avgPrice: bookingInfo.avgPrice,
    servicesOverview: mapOverviewServices(bookingDraft.overviewServices ?? []),
    rebookingWarning: bookingInfo.rebookingWarning,
    originalBookingPaidAmount: bookingInfo.originalBookingPaidAmount,
    totalPackagePrice: bookingInfo.totalPackagePrice,
    avgTotalPackagePrice: bookingInfo.avgTotalPackagePrice,
    accommodationDates: bookingInfo.accommodationDates,
  }
}

const BASKET_ACCOMMODATION_SERVICE_NAME = 'accommodation'
const BASKET_MEALPLAN_SERVICE_NAME = 'mealplan'

function mapTransportLeg({ arrivalDateTime, ...transportLeg }: APIBookingDraftTransportLeg) {
  return {
    ...transportLeg,
    departureDateTime: new Date(transportLeg.departureDateTime),
    ...(arrivalDateTime && { arrivalDateTime: new Date(arrivalDateTime) }),
  }
}

function mapTripDates(accommodationDates?: APIBookingDraftAccommodationDates) {
  if (!accommodationDates)
    return undefined

  const { startDate, endDate, duration } = accommodationDates
  return {
    ...(startDate && { startDate: parseDateForLocalFormatting(String(startDate)) }),
    ...(endDate && { endDate: parseDateForLocalFormatting(String(endDate)) }),
    ...(duration && { duration }),
  }
}

function mapMealPlan(overviewServices: APIBookingDraftServiceOverview[]) {
  return overviewServices
    .filter(service => service.serviceAName?.toLowerCase() === BASKET_MEALPLAN_SERVICE_NAME)
    .flatMap(service => service.lines?.filter(line => !line.isTitle).map(line => line.text) ?? [])
}

function mapNewBasketInformation(bookingDraft: APIBookingDraft) {
  const { accommodationInfo, bookingInfo, bookingPackageDetails } = bookingDraft
  if (!accommodationInfo || !bookingInfo)
    return {}

  const overviewServices = bookingDraft.overviewServices ?? []
  const mealPlan = mapMealPlan(overviewServices)
  const pricesSummary = bookingDraft.pricesSummary

  const renderedServiceNames = new Set([BASKET_ACCOMMODATION_SERVICE_NAME])
  if (mealPlan.length)
    renderedServiceNames.add(BASKET_MEALPLAN_SERVICE_NAME)
  if (bookingPackageDetails?.transfer)
    renderedServiceNames.add('transfer')
  if (bookingPackageDetails?.outboundTransport || bookingPackageDetails?.inboundTransport) {
    renderedServiceNames.add('flight')
    renderedServiceNames.add('bus')
  }

  // The API can supply an already-filtered `bookingPackageDetails.services` list for the basket's
  // dynamic items section. Fall back to filtering the full `overviewServices` list ourselves
  // (excluding rows already rendered as dedicated package-details sections) when it's not
  // supplied yet.
  const items = bookingPackageDetails?.services
    ? mapOverviewServices(bookingPackageDetails.services)
    : mapOverviewServices(overviewServices).filter(
        service => !renderedServiceNames.has(service.serviceAName.toLowerCase()),
      )

  return {
    accommodationName: accommodationInfo.name,
    location: accommodationInfo.location
      .map(location => location)
      .reverse()
      .join(', '),
    imageUrl: accommodationInfo.mainImage?.original,
    countryImageUrl: accommodationInfo.countryImage?.original,
    countryName: accommodationInfo.countryName,
    totalPrice: bookingInfo.totalPrice,
    avgPrice: bookingInfo.avgPrice,
    bookingPackageDetails: {
      tripDates: mapTripDates(bookingInfo.accommodationDates),
      partyComposition: bookingInfo.occupancy,
      room: bookingDraft.rooms.map(room => room.name),
      ...(mealPlan.length && { mealPlan }),
      ...(bookingPackageDetails?.outboundTransport && {
        outboundTransport: mapTransportLeg(bookingPackageDetails.outboundTransport),
      }),
      ...(bookingPackageDetails?.inboundTransport && {
        inboundTransport: mapTransportLeg(bookingPackageDetails.inboundTransport),
      }),
      ...(bookingPackageDetails?.transfer && { transfer: bookingPackageDetails.transfer }),
    },
    items,
    ...(pricesSummary && { priceSummary: mapPriceSummary(pricesSummary) }),
    ...(bookingDraft.isRebooking && {
      rebookingPriceBreakdown: {
        totalPackagePrice: bookingInfo.totalPackagePrice,
        avgTotalPackagePrice: bookingInfo.avgTotalPackagePrice,
        originalBookingPaidAmount: bookingInfo.originalBookingPaidAmount,
        rebookingWarning: bookingInfo.rebookingWarning,
      },
    }),
  }
}

function mapPriceSummary(pricesSummary: APIBookingDraftPricesSummary) {
  return {
    ...(pricesSummary.package && {
      package: {
        serviceTypes: pricesSummary.package.serviceTypes ?? [],
        mealPlan: pricesSummary.package.mealPlan,
        totalPrice: pricesSummary.package.totalPrice,
      },
    }),
    ...(pricesSummary.mandatoryServices && {
      mandatoryServices: pricesSummary.mandatoryServices.map(service => ({
        serviceId: service.serviceId,
        name: service.name,
        totalPrice: service.totalPrice,
      })),
    }),
    extras: pricesSummary.extras,
    discount: pricesSummary.discount,
  }
}

function mapOverviewServices(overviewServices: APIBookingDraftServiceOverview[]) {
  if (!overviewServices)
    return []

  return overviewServices.map(service => ({
    isCollapsedByDefault: service.isCollapsedByDefault,
    title: service.header,
    icon: service.icon,
    serviceAName: service?.serviceAName ?? '',
    ariaLabel: service.ariaLabel,
    lines:
      service?.lines?.map(line => ({
        isTitle: line.isTitle,
        text: line.text,
      })) ?? [],
  }))
}

function mapConditionalMessages(bookingDraft: APIBookingDraft) {
  const { conditionalMessages } = bookingDraft
  if (!conditionalMessages)
    return {}

  return conditionalMessages.reduce((acc, messageGroup) => {
    return {
      ...acc,
      [messageGroup.componentId]: {
        id: messageGroup.componentId,
        banners:
          messageGroup.messages?.map(message => ({
            title: message.title,
            text: message.text,
            type: message.type.toLowerCase(),
          })) ?? [],
      },
    }
  }, {})
}

function mapNavigation(bookingDraft: APIBookingDraft) {
  const { nextButtonText, nextButtonUrl } = bookingDraft.navigation ?? {}
  const { text, url: previousUrl } = bookingDraft?.previousPage ?? {}

  const nextStep = nextButtonUrl
    ? {
        nextStep: {
          text: nextButtonText ?? '',
          url: nextButtonUrl,
        },
      }
    : {}

  const previousStep
    = text && previousUrl
      ? {
          previousStep: {
            text,
            url: previousUrl,
          },
        }
      : {}

  return { ...nextStep, ...previousStep }
}

function mapParticipants({ participants }: APIBookingDraft) {
  if (!participants)
    return {}
  return (
    participants.reduce(
      (acc, participant) => {
        const address = [
          participant.streetName,
          [participant.streetNumber, participant.streetNumberAdditional].filter(Boolean).join(''),
          participant.postalCode,
          participant.cityName,
          participant.country,
        ]
          .filter(Boolean)
          .join(', ')

        return {
          ...acc,
          [participant.id.toString()]: {
            id: participant.id.toString(),
            name: participant.name || participant.firstName,
            birthdate: participant.birthdate,
            roomId: participant.roomId.toString(),
            ageCategory: participant.ageCategory,
            alternativeName: participant.alternativeName?.toLowerCase() || '',
            ...(address && { address }),
            ...(participant.mobilePhoneNumber && { mobilePhoneNumber: mapPhoneNumber(participant.mobilePhoneNumber) }),
            ...(participant.emergencyPhoneNumber && {
              emergencyPhoneNumber: mapPhoneNumber(participant.emergencyPhoneNumber),
            }),
            ...(participant.title && { title: participant.title === 'NotSet' ? '' : participant.title.toLowerCase() }),
            ...(participant.email && { email: participant.email }),
            ...(participant.surname && { surname: participant.surname }),
            ...(participant.participantType && { participantType: participant.participantType }),
          },
        }
      },
      {},
    ) ?? {}
  )
}

function mapParticipantBadgeWarning({ participantsBadge }: APIBookingDraft) {
  if (!participantsBadge)
    return null

  return {
    title: participantsBadge.priceChangedMessage?.title || '',
    text: participantsBadge.priceChangedMessage?.text,
  }
}

function mapRooms({ rooms }: APIBookingDraft) {
  if (!rooms)
    return {}
  return (
    rooms.reduce(
      (acc, room) => ({
        ...acc,
        [room.id.toString()]: {
          id: room.id.toString(),
          name: room.name,
          participantIds: room.participantIds.map(participantId => participantId.toString()),
        },
      }),
      {},
    ) ?? {}
  )
}

function mapGroupInformation(bookingDraft: APIBookingDraft) {
  if (!bookingDraft.groupInfo)
    return null

  return {
    name: bookingDraft.groupInfo?.name,
    masterBookingNumber: bookingDraft.groupInfo?.masterBookingNumber?.toString(),
    numberOfParticipants: bookingDraft.groupInfo?.size?.toString(),
    subscriptionModel: bookingDraft.groupInfo?.subscriptionModel,
    ...(bookingDraft.groupInfo?.hasRoomSelection && {
      roomSelectionIsEnabled: bookingDraft.groupInfo.hasRoomSelection ?? false,
    }),
    ...(bookingDraft.groupInfo?.roomsHavePinCode && {
      pinCodeIsRequiredForRoomSelection: bookingDraft.groupInfo.roomsHavePinCode ?? false,
    }),
    ...(bookingDraft.groupInfo?.roomSelectedInfo
      && bookingDraft.groupInfo?.roomSelectedInfo?.assignationMethod && {
      selectedRoom: {
        method: bookingDraft.groupInfo?.roomSelectedInfo?.assignationMethod,
        name: bookingDraft.groupInfo?.roomSelectedInfo?.name,
        type: bookingDraft.groupInfo?.roomSelectedInfo?.type,
        pinCode: bookingDraft.groupInfo?.roomSelectedInfo?.pinCode,
        isNewRoom: bookingDraft.groupInfo?.roomSelectedInfo?.isNewRoom,
      },
    }),
  }
}

function mapSummaries({ summaryComponents }: APIBookingDraft) {
  if (!summaryComponents)
    return null
  return summaryComponents
    .filter(service => service.type === 'generic' || service.type === 'bus')
    .map((service) => {
      if (service.type === 'generic') {
        const genericService = service as APIBookingDraftGenericSummaryComponent
        return {
          title: genericService.title,
          icon: genericService.icon,
          lines: genericService?.items ?? [],
          type: genericService.type,
        }
      }

      if (service.type === 'bus') {
        const busService = service as APIBookingDraftBusSummaryComponent
        return {
          outboundDate: busService.outboundDate,
          inboundDate: busService.inboundDate,
          type: busService.type,
        }
      }

      return null
    })
    .filter(Boolean)
}

function mapUsps(bookingDraft: APIBookingDraft) {
  if (!bookingDraft.bookingUsps)
    return {}
  return bookingDraft.bookingUsps.reduce((acc, usp) => {
    return {
      ...acc,
      [usp.componentId]: {
        id: usp.componentId,
        usps: usp.items.map(item => ({
          text: item.text,
          ...(item.subtitle && { subtitle: item.subtitle }),
          ...(item.icon && { icon: item.icon }),
        })),
      },
    }
  }, {})
}

function mapECommerceAnalytics(bookingDraft: APIBookingDraft) {
  if (!bookingDraft.eCommerceCheckout)
    return null
  const product = bookingDraft.eCommerceCheckout.products[0]

  return {
    step: bookingDraft.eCommerceCheckout.step,
    accoId: bookingDraft.eCommerceCheckout.accoId.toString(),
    accoName: product.name,
    accoBrand: bookingDraft.eCommerceCheckout.brand,
    breadcrumbs: product.category,
    badges: product.badges ?? '',
    countryId: bookingDraft.eCommerceCheckout.locationId ?? 0,
    accoStars: Number(product.accoStars),
    transportType: product.transport,
    boardType: product.boardType,
    duration: product.duration ?? '',
    rating: Number(product.rating),
    mandatoryPayment: bookingDraft.eCommerceCheckout.mandatoryPayment,
    partyComposition: bookingDraft.eCommerceCheckout.partyComposition,
    totalGuests: bookingDraft.eCommerceCheckout.totalGuests ?? 0,
    departureDate: bookingDraft.eCommerceCheckout.departureDate,
    price: product.price,
    roomType: product.roomType,
    totalStepPrice: bookingDraft.eCommerceCheckout.totalPrice,
    campaignId: bookingDraft.eCommerceCheckout.campaignCode ?? '',
  }
}

function findAgeRestriction(
  participant: APIBookingDraftParticipant,
  restrictions: APIBookingDraftAgeCategoryRestriction[],
): APIBookingDraftAgeCategoryRestriction | undefined {
  if (participant.participantType) {
    const match = restrictions.find(
      r => r.ageCategory === participant.ageCategory && r.participantType === participant.participantType,
    )
    if (match)
      return match
  }
  const fallbackMatches = restrictions.filter(r => r.type === participant.ageCategory)
  return fallbackMatches[fallbackMatches.length - 1]
}

function mapConfigurations(bookingDraft: APIBookingDraft) {
  if (!bookingDraft.ageCategoryRestrictions?.length || !bookingDraft.participants?.length)
    return {}

  const ageRestrictions = bookingDraft.participants.reduce<Record<string, { min: string, max: string }>>(
    (acc, participant) => {
      const match = findAgeRestriction(participant, bookingDraft.ageCategoryRestrictions!)
      if (!match)
        return acc
      return {
        ...acc,
        [participant.id.toString()]: {
          min: match.dateRange.startDate.slice(0, 10),
          max: match.dateRange.endDate.slice(0, 10),
        },
      }
    },
    {},
  )

  return { ageRestrictions }
}

function mapPriceOverviews(bookingDraft: APIBookingDraft) {
  if (!bookingDraft.priceOverviews)
    return {}

  return bookingDraft.priceOverviews.reduce((acc, priceOverview) => {
    return {
      ...acc,
      [priceOverview.componentId]: {
        componentId: priceOverview.componentId,
        warningMessage: priceOverview.warningMessage ?? '',
        mandatoryCostHeader: priceOverview.mandatoryCostsHeader ?? '',
        mandatoryCosts: priceOverview.mandatoryCosts ?? [],
        ...(priceOverview.priceOverviewItems && { items: _mapPriceOverviewItems(priceOverview.priceOverviewItems) }),
      },
    }
  }, {})
}

function _mapPriceOverviewItems(items: APIBookingDraftPriceOverviewItem[]) {
  if (!items)
    return []

  return items.map(item => ({
    value: item.price,
    concept: item.concept,
    total: item.isTotal,
    isDiscount: item.isDiscount,
    isInfo: item.isInfo,
    ...(item.children && { subitems: _mapPriceOverviewSubItems(item.children) }),
  }))
}

function _mapPriceOverviewSubItems(subitems: APIBookingDraftPriceOverviewSubItems[]): CheckoutPriceOverviewSubItems[] {
  return subitems.map(subitem => ({
    concept: subitem.concept,
    isInfo: subitem.isInfo,
    value: subitem.price || '',
    subitems: subitem.children ? _mapPriceOverviewSubItems(subitem.children) : [],
  }))
}

function mapPreferences({ preferences }: APIBookingDraft) {
  if (!preferences)
    return {}

  return preferences.reduce((acc, preference) => {
    return {
      ...acc,
      [preference.id]: {
        id: preference.id,
        selected: preference.isChecked,
        text: preference.text,
        type: preference.type,
        value: preference.value ?? null,
      },
    }
  }, {})
}

function mapTransportInfo(transportInfo: APIBookingDraftTransportInfo) {
  if (!transportInfo)
    return null

  return {
    type: toSnakeCase(transportInfo.type),
    details:
      transportInfo.transports?.map(transport => ({
        name: transport.name,
        code: transport.code,
        isFlexible: transport.isFlexible,
      })) ?? [],
    ...(transportInfo.isOneWayFlight && { isOneWayFlight: transportInfo.isOneWayFlight }),
  }
}

function mapGA4AncillaryImpressions(
  bookingDraft: APIBookingDraft,
  mappers: ServiceMappers,
  configData?: ServiceItem[],
): ImpressionViewAncillariesItem[] {
  const hasServices = !!bookingDraft.services?.length
  const hasRedeemableBookings = !!bookingDraft.user?.redeemableBookings?.length

  if (!hasServices && !hasRedeemableBookings)
    return []

  const allMappers = Object.values(mappers)
  const fallbackOptionMapper = createServiceOptionMapper()

  const bookingRedeemMapper = mappers[ServiceType.BookingRedeem]
  const redeemItems
    = bookingRedeemMapper && hasRedeemableBookings
      ? bookingRedeemMapper.mapGA4ImpressionItems(bookingDraft).map((item, i) => ({ ...item, index: i + 1 }))
      : []

  const redeemNextIndex = redeemItems.length + 1

  const serviceItemsResult: { items: ImpressionViewAncillariesItem[], index: number } = hasServices
    ? bookingDraft.services.reduce<{ items: ImpressionViewAncillariesItem[], index: number }>(
        (acc, apiService) => {
          const serviceMapper = allMappers.find(m => m.getServiceName() === apiService.aName)

          const genericServiceConfig = configData?.find(
            c => c.type === 'GenericService' && c.aName === apiService.aName,
          )

          const luggageServiceConfig = configData?.find(c => c.type === ServiceTypeApi.Luggage)
          const isLuggageService = apiService.aName?.startsWith('LUG_')

          const ga4Config: GA4ImpressionItemsConfig | undefined = genericServiceConfig
            ? {
                showToggleForAllParticipants: genericServiceConfig.serviceConfigurations?.showToggleForAllParticipants,
                toggleForAllParticipantsCheckedByDefault:
                  genericServiceConfig.serviceConfigurations?.toggleForAllParticipantsCheckedByDefault,
              }
            : isLuggageService
              ? {
                  showSpecialLuggage: luggageServiceConfig?.serviceConfigurations?.showSpecialLuggage,
                }
              : undefined

          const items = serviceMapper
            ? serviceMapper.mapGA4ImpressionItems(bookingDraft, ga4Config)
            : mapDefaultGA4ImpressionItems(apiService, bookingDraft, fallbackOptionMapper, ga4Config)

          const newItems = items.map(item => ({ ...item, index: acc.index++ }))

          return { items: [...acc.items, ...newItems], index: acc.index }
        },
        { items: [], index: redeemNextIndex },
      )
    : { items: [], index: redeemNextIndex }

  const { items: serviceItems } = serviceItemsResult

  return [...redeemItems, ...serviceItems]
}

function mapConfirmationDetails(bookingDraft: APIBookingDraft): ConfirmationDetails | undefined {
  const bookingNumber = bookingDraft.confirmationComponents?.[0]?.bookingNumber
  if (bookingNumber == null)
    return undefined

  const { accommodationInfo, bookingInfo } = bookingDraft

  return {
    bookingNumber: bookingNumber.toString(),
    departureDate: bookingInfo?.accommodationDates?.startDate
      ? parseDateForLocalFormatting(String(bookingInfo.accommodationDates.startDate))
      : undefined,
    services: bookingDraft.overviewServices?.map(service => ({
      title: service.header,
      serviceAName: service.serviceAName,
      lines: service.lines?.map(line => ({
        code: line.code,
        text: line.text,
        description: line.descriptionText,
        isIncluded: line.isIncluded,
        participantIds: line.participantIds,
      })) ?? [],
    })) || [],
    ...(accommodationInfo && {
      accommodation: {
        name: accommodationInfo.name,
        imageUrl: accommodationInfo.mainImage?.original,
        address: accommodationInfo.location
          .map(location => location)
          .reverse()
          .join(', '),
        url: accommodationInfo.url,
        geoLocation: {
          latitude: accommodationInfo.geoLocation.latitude,
          longitude: accommodationInfo.geoLocation.longitude,
        },
        tripDates: mapTripDates(bookingInfo?.accommodationDates),
        rooms: bookingDraft.rooms.map(room => room.name),
      },
    }),
  }
}
